import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import * as inboxApi from '../services/founder-inbox-api.js';
import type { InboxAction, TrustEntry, PromptProposal } from '../services/founder-inbox-api.js';
import * as copilotApi from '../services/copilot-api.js';
import '../components/copilot/cp-copilot-panel.js';

/**
 * Founder-Inbox / Freigaben (compass-new Layer 4 · Human-in-the-Loop).
 * Gedraftete Actions (Trust-Stufe 0) freigeben oder ablehnen. Approve schaltet die Action
 * für den ActionExecutor frei (echte Mail / Applai-Upload); Veto bucht ein Trust-Signal.
 */
@customElement('cp-page-approvals')
export class CpPageApprovals extends LitElement {
  createRenderRoot() { return this; }

  @state() private _items: InboxAction[] = [];
  @state() private _counts: Record<string, number> = {};
  @state() private _trust: TrustEntry[] = [];
  @state() private _filter = 'pending';
  @state() private _loading = false;
  @state() private _error = '';
  @state() private _busy = '';
  // Self-Improvement-Loop: per-action Feedback-UI-Zustand (keyed by actionId).
  @state() private _feedbackOpen: Set<string> = new Set();
  @state() private _feedbackText: Record<string, string> = {};
  @state() private _regenerating: Record<string, boolean> = {};
  @state() private _feedbackError: Record<string, string> = {};

  override connectedCallback(): void {
    super.connectedCallback();
    void this._load();
  }

  private async _load(): Promise<void> {
    this._loading = true; this._error = '';
    try {
      const res = await inboxApi.listInbox(this._filter === 'all' ? undefined : this._filter);
      this._items = res.items;
      this._counts = res.counts;
      this._trust = res.trust;
    } catch (e) { this._error = e instanceof Error ? e.message : 'Fehler'; }
    finally { this._loading = false; }
  }

  private async _approve(a: InboxAction): Promise<void> {
    this._busy = a.actionId;
    try { await inboxApi.approveAction(a.actionId); await this._load(); }
    catch (e) { this._error = e instanceof Error ? e.message : 'Fehler'; }
    finally { this._busy = ''; }
  }

  private async _veto(a: InboxAction): Promise<void> {
    if (!confirm(`Action "${a.kind}" ablehnen? Wird als Trust-Signal gebucht.`)) return;
    this._busy = a.actionId;
    try { await inboxApi.vetoAction(a.actionId, false); await this._load(); }
    catch (e) { this._error = e instanceof Error ? e.message : 'Fehler'; }
    finally { this._busy = ''; }
  }

  private _setFilter(f: string): void { this._filter = f; void this._load(); }

  // --- Self-Improvement-Loop: Feedback → Regenerate + Prompt-Vorschlag ---
  private _toggleFeedback(actionId: string): void {
    const next = new Set(this._feedbackOpen);
    if (next.has(actionId)) next.delete(actionId); else next.add(actionId);
    this._feedbackOpen = next;
    this._feedbackError = { ...this._feedbackError, [actionId]: '' };
  }

  private async _regenerate(a: InboxAction): Promise<void> {
    const feedback = (this._feedbackText[a.actionId] ?? '').trim();
    if (!feedback) return;
    this._regenerating = { ...this._regenerating, [a.actionId]: true };
    this._feedbackError = { ...this._feedbackError, [a.actionId]: '' };
    try {
      await inboxApi.regenerateWithFeedback(a.actionId, feedback);
      const next = new Set(this._feedbackOpen); next.delete(a.actionId); this._feedbackOpen = next;
      this._feedbackText = { ...this._feedbackText, [a.actionId]: '' };
      await this._load();
    } catch (e) {
      this._feedbackError = { ...this._feedbackError, [a.actionId]: e instanceof Error ? e.message : 'Fehler' };
    } finally {
      this._regenerating = { ...this._regenerating, [a.actionId]: false };
    }
  }

  private async _regenerateOnly(a: InboxAction): Promise<void> {
    this._regenerating = { ...this._regenerating, [a.actionId]: true };
    this._feedbackError = { ...this._feedbackError, [a.actionId]: '' };
    try {
      await inboxApi.regenerateOnly(a.actionId);
      await this._load();
    } catch (e) {
      this._feedbackError = { ...this._feedbackError, [a.actionId]: e instanceof Error ? e.message : 'Fehler' };
    } finally {
      this._regenerating = { ...this._regenerating, [a.actionId]: false };
    }
  }

  private async _applyPrompt(a: InboxAction): Promise<void> {
    this._busy = a.actionId;
    try { await inboxApi.applyPromptProposal(a.actionId); await this._load(); }
    catch (e) { this._feedbackError = { ...this._feedbackError, [a.actionId]: e instanceof Error ? e.message : 'Fehler' }; }
    finally { this._busy = ''; }
  }

  private async _discardPrompt(a: InboxAction): Promise<void> {
    this._busy = a.actionId;
    try { await inboxApi.discardPromptProposal(a.actionId); await this._load(); }
    catch (e) { this._feedbackError = { ...this._feedbackError, [a.actionId]: e instanceof Error ? e.message : 'Fehler' }; }
    finally { this._busy = ''; }
  }

  /** Feedback-/Regenerate-Block + Prompt-Vorschlag (nur für playbook_approval). */
  private _renderRegen(a: InboxAction) {
    if (a.kind !== 'playbook_approval') return nothing;
    const id = a.actionId;
    const busy = !!this._regenerating[id];
    const err = this._feedbackError[id];
    const proposal: PromptProposal | null | undefined = a.promptProposal;
    const canRegen = a.status === 'pending' && this._payloadPreview(a);

    return html`
      ${canRegen ? (this._feedbackOpen.has(id) ? html`
        <div class="ap-feedback">
          <textarea class="ap-fb-text"
            .value=${this._feedbackText[id] ?? ''}
            placeholder="Feedback zum Entwurf — worauf soll die Neugenerierung achten? (z.B. förmlicher, kürzer, Termin vorschlagen)"
            ?disabled=${busy}
            @input=${(e: Event) => { this._feedbackText = { ...this._feedbackText, [id]: (e.target as HTMLTextAreaElement).value }; }}
          ></textarea>
          <div class="ap-fb-actions">
            <button class="ap-fb-go" ?disabled=${busy || !(this._feedbackText[id] ?? '').trim()} @click=${() => void this._regenerate(a)}>
              ${busy ? 'Regeneriert…' : '⚡ Regenerieren'}
            </button>
            <button class="ap-fb-cancel" ?disabled=${busy} @click=${() => this._toggleFeedback(id)}>Abbrechen</button>
          </div>
        </div>
      ` : html`
        <div class="ap-fb-toggles">
          <button class="ap-fb-toggle" ?disabled=${busy} @click=${() => this._toggleFeedback(id)}>💬 Feedback & Regenerieren</button>
          <button class="ap-fb-toggle" ?disabled=${busy} @click=${() => void this._regenerateOnly(a)} title="Entwurf mit dem aktuellen Prompt neu erzeugen — ohne Feedback">
            ${busy ? 'Regeneriert…' : '🔄 Nur neu generieren'}
          </button>
        </div>
      `) : nothing}
      ${err ? html`<div class="ap-fb-err">${err}</div>` : nothing}
      ${proposal ? html`
        <div class="ap-proposal">
          <div class="ap-proposal-head">🔧 Prompt-Verbesserung vorgeschlagen <span class="ap-proposal-sub">— wirkt auf künftige Runs</span></div>
          <div class="ap-proposal-cols">
            <div><div class="ap-proposal-label">Aktuell</div><div class="ap-proposal-prompt old">${proposal.oldPrompt}</div></div>
            <div><div class="ap-proposal-label">Vorschlag</div><div class="ap-proposal-prompt new">${proposal.newPrompt}</div></div>
          </div>
          <div class="ap-fb-actions">
            <button class="ap-fb-go" ?disabled=${this._busy === id} @click=${() => void this._applyPrompt(a)}>✓ Prompt übernehmen</button>
            <button class="ap-fb-cancel" ?disabled=${this._busy === id} @click=${() => void this._discardPrompt(a)}>Verwerfen</button>
          </div>
        </div>
      ` : nothing}
    `;
  }

  private _statusColor(s: string): string {
    return s === 'pending' ? '#b8860b' : s === 'approved' ? '#1a73e8' : s === 'executed' ? '#2e7d32'
      : s === 'vetoed' ? '#888' : s === 'failed' ? '#c33' : '#bbb';
  }

  private _kindLabel(kind: string): string {
    return kind === 'send_ack_email' ? 'Bestätigungs-Mail senden'
      : kind === 'applai_project_upload' ? 'Projekt zu Applai hochladen'
      : kind === 'playbook_approval' ? 'Playbook-Freigabe' : kind;
  }

  private _payloadSummary(a: InboxAction): string {
    const p = a.payload as Record<string, unknown>;
    const parts: string[] = [];
    if (p.to) parts.push(`an ${String(p.to)}`);
    if (p.subject) parts.push(`„${String(p.subject)}“`);
    if (p.projectTitle) parts.push(String(p.projectTitle));
    if (parts.length) return parts.join(' · ');
    // playbook_approval & co. carry a human-readable summary string.
    return typeof p.summary === 'string' ? p.summary : '';
  }

  private _payloadPreview(a: InboxAction): string {
    const p = a.payload as Record<string, unknown>;
    return typeof p.preview === 'string' ? p.preview : '';
  }

  override render() {
    const filters = ['pending', 'approved', 'executed', 'vetoed', 'failed', 'all'];
    return html`
      <style>
        .ap-page { padding: 1.5rem; max-width: 1450px; margin: 0 auto; display: grid; grid-template-columns: 1fr 380px; gap: 1.5rem; align-items: start; }
        .ap-copilot { position: sticky; top: 1rem; height: calc(100vh - 6rem); }
        @media (max-width: 1100px) { .ap-page { grid-template-columns: 1fr; } .ap-copilot { position: static; height: 520px; } }
        .ap-wrap { font-family: var(--font-family-sans); color: var(--color-text-primary); min-width: 0; }
        .ap-head { display: flex; align-items: baseline; gap: 1rem; margin-bottom: 1rem; }
        .ap-head h1 { font-size: 1.4rem; margin: 0; color: var(--color-text-primary); }
        .ap-sub { color: var(--color-text-secondary); font-size: .9rem; }
        .ap-refresh { margin-left: auto; cursor: pointer; border: 1px solid var(--color-border); background: var(--color-bg-tertiary); color: var(--color-text-primary); border-radius: 6px; padding: .3rem .7rem; }
        .ap-refresh:hover { border-color: var(--color-border-hover); background: var(--color-bg-hover); }
        .ap-filters { display: flex; gap: .4rem; margin-bottom: 1rem; flex-wrap: wrap; }
        .ap-fbtn { cursor: pointer; border: 1px solid var(--color-border); background: var(--color-bg-secondary); border-radius: 999px; padding: .25rem .8rem; font-size: .82rem; color: var(--color-text-secondary); }
        .ap-fbtn:hover { border-color: var(--color-border-hover); }
        .ap-fbtn.active { background: var(--color-accent-primary); color: var(--color-text-inverse); border-color: var(--color-accent-primary); }
        .ap-fbtn .n { opacity: .6; margin-left: .3rem; }
        .ap-card { border: 1px solid var(--color-border); border-radius: 10px; padding: 1rem 1.1rem; margin: .6rem 0; background: var(--color-bg-secondary); }
        .ap-card-top { display: flex; align-items: center; gap: .6rem; }
        .ap-kind { font-weight: 600; font-size: 1rem; color: var(--color-text-primary); }
        .ap-status { font-size: .72rem; text-transform: uppercase; letter-spacing: .05em; padding: .15rem .5rem; border-radius: 999px; color: #fff; }
        .ap-meta { color: var(--color-text-muted); font-size: .82rem; margin: .35rem 0 .2rem; }
        .ap-summary { color: var(--color-text-primary); font-size: .9rem; }
        .ap-preview { margin-top: .5rem; font-size: .88rem; line-height: 1.5; color: var(--color-text-primary); background: var(--color-bg-primary); border: 1px solid var(--color-border); border-radius: 8px; padding: .7rem .85rem; white-space: pre-wrap; }
        .ap-actions { display: flex; gap: .5rem; margin-top: .8rem; }
        .ap-approve { cursor: pointer; border: none; background: var(--color-accent-success); color: #06210f; border-radius: 6px; padding: .45rem 1.1rem; font-weight: 600; }
        .ap-approve:hover { filter: brightness(1.1); }
        .ap-veto { cursor: pointer; border: 1px solid var(--color-accent-error); background: transparent; color: var(--color-accent-error); border-radius: 6px; padding: .45rem 1.1rem; }
        .ap-veto:hover { background: rgba(239,68,68,0.12); }
        .ap-approve:disabled, .ap-veto:disabled { opacity: .5; cursor: default; }
        .ap-result { margin-top: .5rem; font-family: var(--font-family-mono); font-size: .76rem; color: var(--color-text-secondary); background: var(--color-bg-primary); border: 1px solid var(--color-border); border-radius: 6px; padding: .4rem .6rem; white-space: pre-wrap; }
        .ap-fb-toggles { display: flex; gap: .5rem; flex-wrap: wrap; margin-top: .6rem; }
        .ap-fb-toggle { cursor: pointer; border: 1px solid var(--color-border); background: var(--color-bg-tertiary); color: var(--color-text-secondary); border-radius: 6px; padding: .35rem .8rem; font-size: .82rem; }
        .ap-fb-toggle:hover { border-color: var(--color-border-hover); background: var(--color-bg-hover); }
        .ap-fb-toggle:disabled { opacity: .5; cursor: default; }
        .ap-feedback { margin-top: .6rem; padding: .7rem; background: var(--color-bg-primary); border: 1px solid var(--color-border); border-radius: 8px; }
        .ap-fb-text { width: 100%; box-sizing: border-box; min-height: 3.2rem; resize: vertical; border: 1px solid var(--color-border); border-radius: 6px; padding: .5rem .6rem; font-family: var(--font-family-sans); font-size: .86rem; color: var(--color-text-primary); background: var(--color-bg-secondary); }
        .ap-fb-text:focus { outline: 2px solid var(--color-accent-primary); outline-offset: -1px; }
        .ap-fb-text:disabled { opacity: .6; }
        .ap-fb-actions { display: flex; gap: .5rem; margin-top: .55rem; }
        .ap-fb-go { cursor: pointer; border: none; background: #b8860b; color: #fff; border-radius: 6px; padding: .4rem 1rem; font-weight: 600; font-size: .85rem; }
        .ap-fb-go:hover { filter: brightness(1.1); }
        .ap-fb-go:disabled { opacity: .5; cursor: default; }
        .ap-fb-cancel { cursor: pointer; border: 1px solid var(--color-border); background: var(--color-bg-secondary); color: var(--color-text-primary); border-radius: 6px; padding: .4rem 1rem; font-size: .85rem; }
        .ap-fb-cancel:disabled { opacity: .5; cursor: default; }
        .ap-fb-err { margin-top: .4rem; color: var(--color-accent-error); font-size: .82rem; }
        .ap-proposal { margin-top: .6rem; padding: .7rem .8rem; background: var(--color-bg-primary); border: 1px solid var(--color-accent-primary); border-radius: 8px; }
        .ap-proposal-head { font-weight: 600; font-size: .88rem; color: var(--color-text-primary); margin-bottom: .5rem; }
        .ap-proposal-sub { font-weight: 400; color: var(--color-text-muted); font-size: .8rem; }
        .ap-proposal-cols { display: grid; grid-template-columns: 1fr 1fr; gap: .6rem; }
        @media (max-width: 760px) { .ap-proposal-cols { grid-template-columns: 1fr; } }
        .ap-proposal-label { font-size: .72rem; text-transform: uppercase; letter-spacing: .04em; color: var(--color-text-muted); margin-bottom: .2rem; }
        .ap-proposal-prompt { font-size: .8rem; line-height: 1.45; white-space: pre-wrap; border-radius: 6px; padding: .5rem .6rem; border: 1px solid var(--color-border); background: var(--color-bg-secondary); color: var(--color-text-secondary); }
        .ap-proposal-prompt.new { color: var(--color-text-primary); border-color: var(--color-accent-primary); }
        .ap-empty { color: var(--color-text-muted); padding: 2rem 0; }
        .ap-trust { margin-top: 2rem; border-top: 1px solid var(--color-border); padding-top: 1rem; }
        .ap-trust h2 { font-size: 1rem; margin: 0 0 .5rem; color: var(--color-text-secondary); }
        table.ap-t { width: 100%; border-collapse: collapse; font-size: .82rem; }
        table.ap-t th, table.ap-t td { text-align: left; padding: .35rem .5rem; border-bottom: 1px solid var(--color-border); }
        table.ap-t th { color: var(--color-text-muted); font-weight: 600; }
        table.ap-t td { color: var(--color-text-primary); }
      </style>
      <div class="ap-page">
      <div class="ap-wrap">
        <div class="ap-head">
          <h1>Freigaben</h1>
          <span class="ap-sub">Founder-Inbox · Trust-Stufe 0 (nichts geht raus ohne dein Approve)</span>
          <button class="ap-refresh" @click=${() => void this._load()}>↻ Neu laden</button>
        </div>

        <div class="ap-filters">
          ${filters.map((f) => html`
            <button class="ap-fbtn ${this._filter === f ? 'active' : ''}" @click=${() => this._setFilter(f)}>
              ${f}${this._counts[f] ? html`<span class="n">${this._counts[f]}</span>` : nothing}
            </button>
          `)}
        </div>

        ${this._error ? html`<p style="color:#c33">${this._error}</p>` : nothing}
        ${this._loading ? html`<p>Lädt…</p>` : nothing}

        ${!this._loading && this._items.length === 0
          ? html`<div class="ap-empty">Keine Actions in diesem Status. (Drafts entstehen nur mit COMPASS_STAFFING_SHADOW=1 aus eingehenden Staffing-Signalen.)</div>`
          : this._items.map((a) => html`
            <div class="ap-card">
              <div class="ap-card-top">
                <span class="ap-kind">${this._kindLabel(a.kind)}</span>
                <span class="ap-status" style="background:${this._statusColor(a.status)}">${a.status}</span>
                <span style="margin-left:auto; font-size:.72rem; color:#aaa">Autonomie ${a.autonomyLevel}</span>
              </div>
              <div class="ap-meta">${a.strategyId ?? '—'} · ${a.actionId}</div>
              ${this._payloadSummary(a) ? html`<div class="ap-summary">${this._payloadSummary(a)}</div>` : nothing}
              ${this._payloadPreview(a) ? html`<div class="ap-preview">${this._payloadPreview(a)}</div>` : nothing}
              ${a.result ? html`<div class="ap-result">${JSON.stringify(a.result, null, 2)}</div>` : nothing}
              ${a.status === 'pending' ? html`
                <div class="ap-actions">
                  <button class="ap-approve" ?disabled=${this._busy === a.actionId} @click=${() => void this._approve(a)}>✓ Freigeben</button>
                  <button class="ap-veto" ?disabled=${this._busy === a.actionId} @click=${() => void this._veto(a)}>✕ Ablehnen</button>
                </div>
              ` : nothing}
              ${this._renderRegen(a)}
            </div>
          `)}

        ${this._trust.length ? html`
          <div class="ap-trust">
            <h2>Trust-Ledger</h2>
            <table class="ap-t">
              <thead><tr><th>Owner</th><th>Action</th><th>Stufe</th><th>n</th><th>✓</th><th>✕</th><th>Wilson</th></tr></thead>
              <tbody>
                ${this._trust.map((t) => html`
                  <tr>
                    <td>${t.owner}</td><td>${t.actionType}</td><td>${t.autonomyLevel}</td>
                    <td>${t.nActions}</td><td>${t.nApproved}</td><td>${t.nVetoed}</td>
                    <td>${t.wilsonLower.toFixed(3)}</td>
                  </tr>
                `)}
              </tbody>
            </table>
          </div>
        ` : nothing}
      </div>
      <aside class="ap-copilot">
        <cp-copilot-panel
          heading="Freigabe-Copilot"
          subtitle="Bewertet offene Actions, empfiehlt approve/veto. Bestätigung bei dir."
          placeholder="z.B. Sollte ich die Ack-Mail freigeben?"
          example="Was bewirkt die offene Action konkret — und empfiehlst du Freigabe oder Ablehnung?"
          .send=${(m: string) => copilotApi.askApprovals(m)}
          .apply=${(op: Record<string, unknown>) => copilotApi.applyApprovalOp(op as unknown as copilotApi.ApprovalOp)}
          .opLabel=${(op: Record<string, unknown>) => `${op.op === 'approve' ? 'Freigeben' : 'Ablehnen'} ${String(op.actionId)}${op.reason ? ` — ${String(op.reason)}` : ''}`}
          @data-changed=${() => void this._load()}
        ></cp-copilot-panel>
      </aside>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-page-approvals': CpPageApprovals;
  }
}
